/**
 * One step, in plain words, with what the signed-in person may do about it.
 * The family sees the goal and the home practices; the therapist gets the
 * professional view (criterion, probes) and the confirm / step-back actions.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Api, ApiError } from '@/core/api';
import { formatAge, formatJalali } from '@/core/jalali';
import { STATUS_FA, type NodeState, type Practice } from '@/core/types';
import { useProfile } from '@/app/ProfileShell';

const ERR_FA: Record<string, string> = {
  PROFILE_BRANCH_NOT_GRANTED: 'این شاخه در دسترس شما نیست.',
  PROFILE_FORBIDDEN: 'این کار برای نقش شما مجاز نیست.',
  PLAN_ITEM_ALREADY_ACTIVE: 'این تمرین از قبل در برنامه است.',
};

export function NodeSheet({
  profileId,
  node,
  onClose,
}: {
  profileId: string;
  node: NodeState;
  onClose: () => void;
}) {
  const profile = useProfile();
  const qc = useQueryClient();
  const full = useQuery({ queryKey: ['node', node.key], queryFn: () => Api.node(node.key) });
  const [pro, setPro] = useState(profile.myRole === 'practitioner');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['state', profileId] });
    void qc.invalidateQueries({ queryKey: ['profile', profileId] });
    void qc.invalidateQueries({ queryKey: ['today', profileId] });
  };
  const fail = (e: unknown) =>
    setErr(e instanceof ApiError ? (ERR_FA[e.code] ?? e.code) : 'ثبت نشد');

  const assess = useMutation({
    mutationFn: (verdict: 'achieved' | 'emerging' | 'not_yet') =>
      Api.assess(profileId, {
        nodeKey: node.key,
        verdict,
        method: profile.myRole === 'practitioner' ? 'observation' : 'report',
        setting: profile.myRole === 'practitioner' ? 'clinic' : 'home',
      }),
    onSuccess: (r) => {
      setErr(null);
      setMsg(
        r.confirming
          ? 'ثبت شد و تأیید شد.'
          : 'ثبت شد. تا درمانگر تأیید کند، به‌عنوان «شما دیده‌اید» نمایش داده می‌شود.',
      );
      refresh();
    },
    onError: fail,
  });
  const logPractice = useMutation({
    mutationFn: (p: Practice) =>
      Api.logPractice(profileId, {
        nodeKey: node.key,
        practiceKey: p.key,
        setting: profile.myRole === 'practitioner' ? 'clinic' : 'home',
        result: 'went_well',
        clientEventId: `web-${crypto.randomUUID()}`,
      }),
    onSuccess: () => {
      setErr(null);
      setMsg('تمرین ثبت شد.');
      refresh();
    },
    onError: fail,
  });
  const addPlan = useMutation({
    mutationFn: (p: Practice) =>
      Api.addPlan(profileId, {
        nodeKey: node.key,
        practiceKey: p.key,
        setting: p.setting === 'clinic' ? 'clinic' : 'home',
        timesPerWeek: 5,
      }),
    onSuccess: () => {
      setErr(null);
      setMsg('به برنامهٔ امروز اضافه شد.');
      refresh();
    },
    onError: fail,
  });
  const revert = useMutation({
    mutationFn: () => Api.revert(profileId, { nodeKey: node.key, toVerdict: 'emerging', reason }),
    onSuccess: () => {
      setErr(null);
      setMsg('به «در حال یادگیری» برگشت.');
      setReason('');
      refresh();
    },
    onError: fail,
  });
  const lost = useMutation({
    mutationFn: () => Api.lost(profileId, { nodeKey: node.key, reason }),
    onSuccess: () => {
      setErr(null);
      setMsg('ثبت شد: از دست رفته.');
      setReason('');
      refresh();
    },
    onError: fail,
  });

  const canWrite = profile.myLevel !== 'read';
  const canConfirm =
    profile.myLevel === 'owner' || profile.myLevel === 'member' || profile.myLevel === 'confirm';
  const practices = (full.data?.practices ?? []).filter((p) =>
    profile.myRole === 'practitioner' ? true : p.setting !== 'clinic',
  );

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="sheet"
        role="dialog"
        aria-label={node.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row spread">
          <span className="overline">{node.label}</span>
          <span className={`chip ${node.status}`}>{STATUS_FA[node.status]}</span>
        </div>
        <h3 style={{ fontSize: 'var(--fs-section)', fontWeight: 700 }}>{node.title}</h3>
        {node.typicalAge && (
          <div className="help">
            معمولاً بین {formatAge(node.typicalAge.fromMonths)} تا{' '}
            {formatAge(node.typicalAge.toMonths)}
            {node.lastEventAt ? ` · آخرین ثبت ${formatJalali(node.lastEventAt)}` : ''}
          </div>
        )}
        {full.data && <p>{full.data.concept}</p>}
        {node.reportedBy.length > 0 && node.status !== 'achieved' && (
          <div className="notice">خانواده این را دیده است؛ هنوز تأیید درمانگر ندارد.</div>
        )}

        {msg && <div className="notice">{msg}</div>}
        {err && <div className="error">{err}</div>}

        {canWrite && node.status !== 'not_due' && (
          <div className="row">
            {canConfirm && node.status !== 'achieved' && (
              <button
                type="button"
                className="btn primary"
                disabled={assess.isPending}
                onClick={() => assess.mutate('achieved')}
              >
                {profile.myRole === 'practitioner' ? 'تأیید می‌کنم' : 'بلد است'}
              </button>
            )}
            {canConfirm && node.status !== 'emerging' && (
              <button
                type="button"
                className="btn quiet"
                disabled={assess.isPending}
                onClick={() => assess.mutate('emerging')}
              >
                در حال یادگیری
              </button>
            )}
            {canConfirm && node.status === 'unknown' && (
              <button
                type="button"
                className="btn quiet"
                disabled={assess.isPending}
                onClick={() => assess.mutate('not_yet')}
              >
                هنوز نه
              </button>
            )}
          </div>
        )}

        {practices.length > 0 && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="overline">تمرین‌ها</div>
            {practices.map((p) => (
              <div key={p.key} className="card" style={{ padding: 12, gap: 6 }}>
                <strong>{p.title}</strong>
                <p className="help">{p.how}</p>
                {p.dose && <p className="help">{p.dose}</p>}
                {canWrite && (
                  <div className="row">
                    <button
                      type="button"
                      className="btn honey"
                      disabled={logPractice.isPending}
                      onClick={() => logPractice.mutate(p)}
                    >
                      ✓ انجام دادیم
                    </button>
                    <button
                      type="button"
                      className="btn quiet"
                      disabled={addPlan.isPending}
                      onClick={() => addPlan.mutate(p)}
                    >
                      + به برنامه
                    </button>
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {profile.myRole === 'practitioner' || profile.myRole === 'self' ? (
          <button type="button" className="btn quiet" onClick={() => setPro((v) => !v)}>
            {pro ? 'پنهان‌کردن نمای حرفه‌ای' : 'نمای حرفه‌ای'}
          </button>
        ) : null}

        {pro && full.data && (
          <section className="card" style={{ gap: 6 }}>
            <div className="overline">معیار پذیرش</div>
            <p>
              <strong>رفتار:</strong> {full.data.acceptance.behavior}
            </p>
            <p>
              <strong>آستانه:</strong> {full.data.acceptance.threshold}
            </p>
            <p>
              <strong>استقلال:</strong> {full.data.acceptance.independence}
            </p>
            <p>
              <strong>تعمیم:</strong> {full.data.acceptance.generalization}
            </p>
            <p>
              <strong>تأیید با:</strong> {full.data.acceptance.confirmedBy}
            </p>
            {full.data.evidence
              .filter((e) => e.source === 'probe')
              .map((e, i) => (
                <p key={i} className="help">
                  <strong>آزمون کوتاه — {e.name}:</strong> {e.how} <em>({e.pass})</em>
                </p>
              ))}
            {canConfirm && (node.status === 'achieved' || node.status === 'reported') && (
              <>
                <label className="field">
                  <span>دلیل برگشت یا از دست رفتن</span>
                  <textarea
                    className="input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <div className="row">
                  <button
                    type="button"
                    className="btn quiet"
                    disabled={reason.length < 2 || revert.isPending}
                    onClick={() => revert.mutate()}
                  >
                    یک قدم عقب
                  </button>
                  <button
                    type="button"
                    className="btn danger"
                    disabled={reason.length < 2 || lost.isPending}
                    onClick={() => lost.mutate()}
                  >
                    از دست رفته
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        <button type="button" className="btn quiet block" onClick={onClose}>
          بستن
        </button>
      </div>
    </div>
  );
}
